import { Permission } from 'shared'
import { CreateHeaderActionOptions } from 'components/CIMSTable/CIMSTableContext/types/actions/header'
import { LabTech, useLabTechsContext } from '../../LabTechsContext'
import { Input } from 'antd'
import CIMSFormItem from 'components/CIMSFormItem'
import UserSelect from 'components/CIMSSelect/common/UserSelect'
import { useState } from 'react'
import { useGlobalState } from 'context/GlobalStateContext'
import CIMSSelect from 'components/CIMSSelect'
import { useCreateLabTechMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { HelpMessage } from 'utils/constants'
import { CreateEditValues } from '../../types'
import { useCatchDuplicateKeyError } from '../useCatchDuplicateKeyError'
import TestingLabSelect from 'components/CIMSSelect/common/TestingLabSelect'
import { useUserSelectContext } from 'components/CIMSSelect/common/UserSelect/UserSelectContext'
import { useTestingLabSelectContext } from 'components/CIMSSelect/common/TestingLabSelect/TestingLabSelectContext'
import { errorHandler } from 'hooks/errorHandler'

export function useCreate(): CreateHeaderActionOptions<LabTech> {
  const { fetching, refetch } = useLabTechsContext()
  const types = ['User', 'Generic'] as const
  const [techType, setTechType] = useState<(typeof types)[any]>('User')
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const [create] = useCreateLabTechMutation({
    ...DefaultMutationHookOptions,
    ...useCatchDuplicateKeyError(
      'created',
      ({ createLabTech }) => createLabTech.labTech
    ),
  })
  const userSelectContext = useUserSelectContext()
  const testingLabSelectContext = useTestingLabSelectContext()
  return {
    entity: 'Lab Tech',
    permission: Permission.CreateLabTech,
    loading: fetching,
    modal: {
      type: 'simple',
      body: (
        <>
          <CIMSFormItem
            label="Type"
            help="User Lab Techs are Users made Lab Techs while Generic Lab Techs just need a name"
          >
            <CIMSSelect
              value={techType}
              simple
              options={types.map((value) => ({ label: value, value }))}
              onChange={(value) => setTechType(value as typeof techType)}
            />
          </CIMSFormItem>
          {techType === 'Generic' && (
            <CIMSFormItem
              name="name"
              label="Name"
              requiredMessage="Please enter a name"
              help={HelpMessage.Name}
            >
              <Input allowClear />
            </CIMSFormItem>
          )}
          {techType === 'User' && (
            <CIMSFormItem
              name="user"
              label="User"
              requiredMessage="Please select a user"
              help={HelpMessage.Concrete.LabTechs.LabTechs.User()}
            >
              <UserSelect />
            </CIMSFormItem>
          )}
          <CIMSFormItem label="Testing Labs" name="testingLabs">
            <TestingLabSelect multiple />
          </CIMSFormItem>
        </>
      ),
      onOk: async ({ name, user, testingLabs }: CreateEditValues) => {
        if (!projectId) return errorHandler('Project id must be defined')

        await create({
          variables: {
            input: {
              projectId,
              name,
              userId: userSelectContext.fromLabeledValue(user),
              testingLabIds:
                testingLabSelectContext.fromLabeledValues(testingLabs),
            },
          },
        })
        refetch()
      },
    },
  }
}
